/**
 * @fileoverview a thin wrapper around sessionStorage, the session-scoped sibling of
 * `@lib/storageManager` (localStorage). Use it for state that should persist across navigation
 * within one browser session but reset in a new session (e.g. per-session banner dismissals).
 * Key names live in `sessionStorageKeys` in `./constants`.
 *
 * Some contexts block storage entirely (Safari private mode, disabled cookies, sandboxed iframes);
 * accessing or writing it then throws. All methods guard on `sessionStorageIsSupported()`, and
 * callers can check it directly to hide UI whose state can't be persisted (e.g. a dismiss control).
 */

import type { SessionStorageKey } from "./constants";
import { supportProbeKey } from "./constants";

export class SessionStorageManager {
  /** Memoized support probe result; whether storage is blocked does not change within a session. */
  private static supported: boolean | undefined;

  /**
   * Whether `window.sessionStorage` can be read and written this browser session. The underlying
   * probe runs once and is memoized.
   *
   * @returns true when sessionStorage reads and writes succeed
   */
  public static sessionStorageIsSupported(): boolean {
    if (SessionStorageManager.supported === undefined) {
      try {
        window.sessionStorage.setItem(supportProbeKey, "1");
        window.sessionStorage.removeItem(supportProbeKey);
        SessionStorageManager.supported = true;
      } catch {
        SessionStorageManager.supported = false;
      }
    }

    return SessionStorageManager.supported;
  }

  /**
   * Reads the value stored under `key`, deserializing JSON written by {@link set}. Values that
   * were not stored as JSON are returned as the raw string.
   *
   * @returns the stored value, or null when storage is unsupported or the key is absent
   */
  public static get<T = unknown>(key: SessionStorageKey): T | null {
    if (!SessionStorageManager.sessionStorageIsSupported()) {
      return null;
    }

    const stored = window.sessionStorage.getItem(key);
    if (stored == null) {
      return null;
    }

    try {
      return JSON.parse(stored);
    } catch {
      return stored as T;
    }
  }

  /** Stores `value` under `key` as JSON. No-op when storage is unsupported or `value` is null. */
  public static set<T>(key: SessionStorageKey, value: T): void {
    if (SessionStorageManager.sessionStorageIsSupported() && value != null) {
      try {
        window.sessionStorage.setItem(key, JSON.stringify(value));
      } catch {
        return;
      }
    }
  }

  /** Removes any value stored under `key`. No-op when storage is unsupported. */
  public static remove(key: SessionStorageKey): void {
    if (!SessionStorageManager.sessionStorageIsSupported()) {
      return;
    }

    window.sessionStorage.removeItem(key);
  }
}
